import React, { useState, useEffect, useRef } from 'react';

const DashboardURL = ({ selectedChannels, applicationId, applicationSecret }) => {
  const [dashboardAlias, setDashboardAlias] = useState('');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [dashboardCreated, setDashboardCreated] = useState(false);
  const [dashboardHistory, setDashboardHistory] = useState([]);
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const [copiedHistoryIndex, setCopiedHistoryIndex] = useState(null);

  // Stable fingerprint of the current config for change detection
  const configFingerprint = JSON.stringify(
    selectedChannels.map(ch => ({ id: ch.channelId, alias: ch.alias, es: ch.expectedStreams, sids: ch.streamIds }))
  );
  const createdWithFingerprint = useRef(null);

  useEffect(() => {
    if (createdWithFingerprint.current !== null && createdWithFingerprint.current !== configFingerprint) {
      setDashboardCreated(false);
      setDashboardAlias('');
      setError('');
    }
  }, [configFingerprint]);

  const createDashboard = async () => {
    setIsCreating(true);
    setError('');
    try {
      const authToken = btoa(`${applicationId}:${applicationSecret}`);

      const response = await fetch('/api/generate-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Basic ${authToken}`
        },
        body: JSON.stringify({
          channels: selectedChannels.map(channel => ({
            channelId: channel.channelId,
            alias: channel.alias,
            expectedStreams: channel.expectedStreams ?? 1,
            streamIds: channel.streamIds
          }))
        })
      });

      if (!response.ok) {
        let message = 'Failed to generate dashboard configuration';
        try {
          const data = await response.json();
          if (data.message) message = data.message;
        } catch (_) { /* non-JSON error response (e.g. Cloudflare edge page) */ }
        throw new Error(message);
      }

      const { alias } = await response.json();
      if (!alias) {
        throw new Error('No alias received from server');
      }
      setDashboardAlias(alias);
      setDashboardCreated(true);
      createdWithFingerprint.current = configFingerprint;
      setDashboardHistory(prev => [{
        url: `${window.location.origin}/dashboard?alias=${alias}`,
        alias,
        channelCount: selectedChannels.length,
        channelNames: selectedChannels.map(ch => ch.alias || ch.channelId),
        createdAt: new Date()
      }, ...prev]);
      setError('');
      sessionStorage.removeItem('phenixCredentials');
    } catch (err) {
      console.error('Dashboard creation error:', err);
      setError(err.message);
      setDashboardAlias('');
      setDashboardCreated(false);
      createdWithFingerprint.current = configFingerprint;
    } finally {
      setIsCreating(false);
    }
  };

  const getDashboardUrl = (alias) => {
    const baseUrl = window.location.origin;
    const a = alias || dashboardAlias;
    return a ? `${baseUrl}/dashboard?alias=${a}` : '';
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getDashboardUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="border-t bg-white p-2 flex-none">
    {selectedChannels.length === 0 ? (
      <div className="text-gray-500">
        Select channels to generate a dashboard URL
      </div>
    ) : error ? (
        <div className="text-red-600 mb-2">
          {error}
          <button
            onClick={createDashboard}
            className="ml-3 px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            Retry
          </button>
        </div>
      ) : dashboardCreated && dashboardAlias ? (
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border">
            <button
              onClick={handleCopy}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-nowrap"
            >
              {copied ? 'Copied!' : 'Copy URL'}
            </button>
            <a
              href={getDashboardUrl()}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate text-blue-600 hover:text-blue-800 hover:underline px-2"
            >
              {getDashboardUrl()}
            </a>
          </div>
          <div className="mt-1 text-sm text-gray-500">
            Dashboard URL includes {selectedChannels.length} channel{selectedChannels.length !== 1 ? 's' : ''}
          </div>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto">
          <button
            onClick={createDashboard}
            disabled={isCreating}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? 'Creating Dashboard...' : 'Create Dashboard'}
          </button>
          <div className="mt-1 text-sm text-gray-500">
            {selectedChannels.length} channel{selectedChannels.length !== 1 ? 's' : ''} configured — click to create dashboard
          </div>
        </div>
      )}
      {dashboardHistory.length > 0 && (
        <div className="mt-2 max-w-4xl mx-auto">
          <button
            onClick={() => setHistoryExpanded(!historyExpanded)}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <span className={`transform transition-transform ${historyExpanded ? 'rotate-90' : ''}`}>&#9654;</span>
            History ({dashboardHistory.length})
          </button>
          {historyExpanded && (
            <div className="mt-1 space-y-1">
              {dashboardHistory.map((entry, i) => (
                <div key={entry.alias} className="flex items-center gap-2 p-1.5 bg-gray-50 rounded border text-sm">
                  <button
                    onClick={async () => {
                      await navigator.clipboard.writeText(entry.url);
                      setCopiedHistoryIndex(i);
                      setTimeout(() => setCopiedHistoryIndex(null), 2000);
                    }}
                    className="px-2 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs whitespace-nowrap"
                  >
                    {copiedHistoryIndex === i ? 'Copied!' : 'Copy'}
                  </button>
                  <span className="truncate text-gray-600">{entry.channelNames.join(', ')}</span>
                  <span className="text-gray-400 text-xs whitespace-nowrap">
                    {entry.createdAt.toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardURL;